import React, { Suspense, useEffect } from "react";
import { initializeNativePlugins } from "@/utils/nativePlugins";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { initFacebookPixel, trackPageView } from "@/lib/facebookPixel";
import NotFound from "./pages/NotFound";
import RootRedirect from "./components/routing/RootRedirect";
import NativeEntryGuard from "./components/routing/NativeEntryGuard";
import AndroidBackHandler from "./components/native/AndroidBackHandler";
import AppUrlListener from "./components/native/AppUrlListener";
import AppLifecycleHandler from "./components/native/AppLifecycleHandler";
import AnimatedSplash from "./components/native/AnimatedSplash";
import { useSplashScreen } from "./hooks/useSplashScreen";

import NativeRoleSelectorPage from "./pages/native/NativeRoleSelectorPage";
import SellerAuthLayout from "./layouts/SellerAuthLayout";
import BuyerAuthLayout from "./layouts/BuyerAuthLayout";
import DashboardLayout from "./layouts/DashboardLayout";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import DashboardPage from "./pages/dashboard/DashboardPage";
import NewPropertyPage from "./pages/properties/NewPropertyPage";
import ActiveDealsPage from "./pages/properties/ActiveDealsPage";
import ClosedDealsPage from "./pages/properties/ClosedDealsPage";
import PropertyDetailPage from "./pages/properties/PropertyDetailPage";
import EditPropertyPage from "./pages/properties/EditPropertyPage";
import ProfilePage from "./pages/user/ProfilePage";
import LandingPage from "./pages/marketing/LandingPage";
import PricingPage from "./pages/marketing/PricingPage";
import ContactPage from "./pages/ContactPage";
import BuyerLandingPage from "./pages/buyer/BuyerLandingPage";
import BuyerLoginPage from "./pages/buyer/BuyerLoginPage";
import BuyerRegisterPage from "./pages/buyer/BuyerRegisterPage";
import BuyerPricingPage from "./pages/buyer/BuyerPricingPage";
import BuyerForgotPasswordPage from "./pages/buyer/BuyerForgotPasswordPage";

// Lazy load buyer pages to reduce initial bundle size
const BuyerDashboardPage = React.lazy(() => import("./pages/buyer/BuyerDashboardPage"));
const PropertySearchPage = React.lazy(() => import("./pages/buyer/PropertySearchPage"));
const SavedPropertiesPage = React.lazy(() => import("./pages/buyer/SavedPropertiesPage"));
const BuyerNotificationsPage = React.lazy(() => import("./pages/buyer/BuyerNotificationsPage"));
const FavoriteSellersPage = React.lazy(() => import("./pages/buyer/FavoriteSellersPage"));
const SellerListingsPage = React.lazy(() => import("./pages/buyer/SellerListingsPage"));
const BuyerPropertyDetailPage = React.lazy(() => import("./pages/buyer/BuyerPropertyDetailPage"));
const BuyerOffersPage = React.lazy(() => import("./pages/buyer/BuyerOffersPage"));
const BlogPage = React.lazy(() => import("./pages/public/BlogPage"));
const BlogArticlePage = React.lazy(() => import("./pages/public/BlogArticlePage"));
import PrivacyPolicyPage from "./pages/legal/PrivacyPolicyPage";
import TermsAndConditionsPage from "./pages/legal/TermsAndConditionsPage";
import BuyerLayout from "./layouts/BuyerLayout";
import PublicListingsPage from "./pages/public/PublicListingsPage";
import SellerFAQPage from "./pages/public/SellerFAQPage";
import BuyerFAQPage from "./pages/public/BuyerFAQPage";
import PublicPropertyDetailPage from "./pages/public/PublicPropertyDetailPage";
import { AuthProvider } from "./contexts/AuthContext";
import { BuyerProvider } from "./contexts/BuyerContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { TourProvider } from "./contexts/TourContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import BuyerProtectedRoute from "./components/auth/BuyerProtectedRoute";
import NotificationsPage from "./pages/seller/NotificationsPage";
import SearchBuyersPage from "./pages/seller/SearchBuyersPage";
import BuyerListsPage from "./pages/seller/BuyerListsPage";
import BuyerListDetailPage from "./pages/seller/BuyerListDetailPage";
import EditBuyerListPage from "./pages/seller/EditBuyerListPage";
import HelpCenterPage from "./pages/seller/HelpCenterPage";
import SellerHelpHomePage from "./pages/seller/SellerHelpHomePage";
import ManageSubscriptionPage from "./pages/seller/ManageSubscriptionPage";

// Lazy load buyer help and subscription pages
const BuyerHelpCenterPage = React.lazy(() => import("./pages/buyer/BuyerHelpCenterPage"));
const BuyerHelpHomePage = React.lazy(() => import("./pages/buyer/BuyerHelpHomePage"));
const BuyerManageSubscriptionPage = React.lazy(() => import("./pages/buyer/BuyerManageSubscriptionPage"));
const BuyerUpgradePage = React.lazy(() => import("./pages/buyer/BuyerUpgradePage"));
const RepairEstimatorPage = React.lazy(() => import("./pages/buyer/RepairEstimatorPage"));
const ProfitCalculatorPage = React.lazy(() => import("./pages/buyer/ProfitCalculatorPage"));
import SellerUpgradePage from "./pages/seller/SellerUpgradePage";
import SellerOffersPage from "./pages/seller/SellerOffersPage";
import SellerPropertySearchPage from "./pages/seller/SellerPropertySearchPage";
import DealTrackerPage from "./pages/seller/DealTrackerPage";
import SellerToolsPage from "./pages/seller/SellerToolsPage";
import ScrollToTop from "./components/common/ScrollToTop";
import FacebookPixelPageView from "./components/tracking/FacebookPixelPageView";
import AdminProtectedRoute from "./components/auth/AdminProtectedRoute";
import AdminLayout from "./layouts/AdminLayout";
import ConsumerLandingPage from "./pages/consumer/ConsumerLandingPage";
import ConsumerGetStartedPage from "./pages/consumer/ConsumerGetStartedPage";
import ConsumerLoginPage from "./pages/consumer/ConsumerLoginPage";
import ConsumerForgotPasswordPage from "./pages/consumer/ConsumerForgotPasswordPage";
import ConsumerProtectedRoute from "./components/auth/ConsumerProtectedRoute";
import { ConsumerProvider } from "./contexts/ConsumerContext";
const ConsumerDashboardPage = React.lazy(() => import("./pages/consumer/ConsumerDashboardPage"));
import InstallPage from "./pages/InstallPage";
const MarketDataPage = React.lazy(() => import("./pages/market/MarketDataPage"));
import PropertyValuationPage from "./pages/public/PropertyValuationPage";
import SellerNetSheetPage from "./pages/tools/SellerNetSheetPage";
import GetOfferPage from "./pages/tools/GetOfferPage";
import EmailChangeCallbackPage from "./pages/auth/EmailChangeCallbackPage";

// Lazy load admin pages to reduce initial bundle size
const AdminDashboardPage = React.lazy(() => import("./pages/admin/AdminDashboardPage"));
const AdminUsersPage = React.lazy(() => import("./pages/admin/AdminUsersPage"));
const AdminSellersPage = React.lazy(() => import("./pages/admin/AdminSellersPage"));
const AdminBuyersPage = React.lazy(() => import("./pages/admin/AdminBuyersPage"));
const AdminPropertiesPage = React.lazy(() => import("./pages/admin/AdminPropertiesPage"));
const AdminSubscriptionsPage = React.lazy(() => import("./pages/admin/AdminSubscriptionsPage"));
const AdminAnalyticsPage = React.lazy(() => import("./pages/admin/AdminAnalyticsPage"));
const AdminSettingsPage = React.lazy(() => import("./pages/admin/AdminSettingsPage"));
const AdminLeadsPage = React.lazy(() => import("./pages/admin/AdminLeadsPage"));
const AdminUserManagementPage = React.lazy(() => import("./pages/admin/AdminUserManagementPage"));
const AdminPromoCodesPage = React.lazy(() => import("./pages/admin/AdminPromoCodesPage"));
const AdminWebhookEventsPage = React.lazy(() => import("./pages/admin/AdminWebhookEventsPage"));
const AdminAlertsPage = React.lazy(() => import("./pages/admin/AdminAlertsPage"));
const AdminEdgeFunctionLogsPage = React.lazy(() => import("./pages/admin/AdminEdgeFunctionLogsPage"));
const AdminStripeConfigPage = React.lazy(() => import("./pages/admin/AdminStripeConfigPage"));
const AdminApiStatusPage = React.lazy(() => import("./pages/admin/AdminApiStatusPage"));
const AdminAbandonmentPage = React.lazy(() => import("./pages/admin/AdminAbandonmentPage"));
const AdminRateLimitPage = React.lazy(() => import("./pages/admin/AdminRateLimitPage"));
const AdminMaintenanceChecklistPage = React.lazy(() => import("./pages/admin/AdminMaintenanceChecklistPage"));
const AdminOffersPage = React.lazy(() => import("./pages/admin/AdminOffersPage"));
const AdminBrandKitPage = React.lazy(() => import("./pages/admin/AdminBrandKitPage"));
const AdminBlogPage = React.lazy(() => import("./pages/admin/AdminBlogPage"));
const AdminBlogApiPage = React.lazy(() => import("./pages/admin/AdminBlogApiPage"));
const AdminBlogApiLogsPage = React.lazy(() => import("./pages/admin/AdminBlogApiLogsPage"));
const AdminContractsPage = React.lazy(() => import("./pages/admin/AdminContractsPage"));
const AdminHomeownersPage = React.lazy(() => import("./pages/admin/AdminHomeownersPage"));

// Admin loading skeleton component
const AdminLoadingSkeleton = () => (
  <div className="flex items-center justify-center h-64">
    <Loader2 className="h-8 w-8 animate-spin text-godyspo-gold" />
  </div>
);

const queryClient = new QueryClient();

const App = () => {
  const { showSplash, hideSplash } = useSplashScreen();

  useEffect(() => {
    initializeNativePlugins();
    // Initialize Facebook Pixel on app load
    initFacebookPixel();
  }, []);

  // Show animated splash on native platforms
  if (showSplash) {
    return <AnimatedSplash onComplete={hideSplash} />;
  }

  return (
    <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-destructive"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Something went wrong</h1>
            <p className="text-muted-foreground">
              We've been notified and are working to fix the issue. Please try reloading the page.
            </p>
          </div>
          
          <button
            onClick={() => {
              // Clear any stuck registration state (wrapped in try-catch for Safari Private Mode)
              try {
                localStorage.removeItem('buyer_registration_data');
                localStorage.removeItem('buyer_email_verification_pending');
                localStorage.removeItem('seller_registration_data');
              } catch (e) {
                // Ignore storage errors in Safari Private Mode
                console.warn('localStorage clear failed:', e);
              }
              
              // Determine redirect based on current URL
              const isBuyerPath = window.location.pathname.startsWith('/buyer');
              const isSellerPath = window.location.pathname.startsWith('/seller');
              
              if (isBuyerPath) {
                window.location.href = '/buyer/dashboard';
              } else if (isSellerPath) {
                window.location.href = '/seller/dashboard';
              } else {
                window.location.href = '/';
              }
            }}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Try Again
          </button>
          
          {import.meta.env.DEV && error && (
            <details className="mt-6 text-left">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                Error details (development only)
              </summary>
              <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-auto text-left">
                {error.toString()}
                {error.stack}
              </pre>
            </details>
          )}
        </div>
      </div>
    )}
    showDialog={false}
  >
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <BrowserRouter>
            
            <NativeEntryGuard />
            <AndroidBackHandler />
            <AppUrlListener />
            <AppLifecycleHandler />
            <ScrollToTop />
            <FacebookPixelPageView />
            <AuthProvider>
              <BuyerProvider>
                <SubscriptionProvider>
                  <TourProvider>
                    <Toaster />
                    <Sonner />
                    <Routes>
                  {/* Root uses smart redirect based on platform */}
                  <Route path="/" element={<RootRedirect />} />
                  
                  {/* Native app role selector */}
                  <Route path="/native/role-select" element={<NativeRoleSelectorPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/install" element={<InstallPage />} />
                  <Route path="/market" element={<Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}><MarketDataPage /></Suspense>} />
              
          {/* Admin Routes - Lazy loaded for bundle size optimization */}
          <Route element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
            <Route path="/admin/dashboard" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminDashboardPage /></Suspense>} />
            <Route path="/admin/users" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminUsersPage /></Suspense>} />
            <Route path="/admin/sellers" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminSellersPage /></Suspense>} />
            <Route path="/admin/properties" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminPropertiesPage /></Suspense>} />
            <Route path="/admin/offers" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminOffersPage /></Suspense>} />
            <Route path="/admin/buyers" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminBuyersPage /></Suspense>} />
            <Route path="/admin/subscriptions" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminSubscriptionsPage /></Suspense>} />
            <Route path="/admin/stripe-config" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminStripeConfigPage /></Suspense>} />
            <Route path="/admin/promo-codes" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminPromoCodesPage /></Suspense>} />
            <Route path="/admin/webhooks" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminWebhookEventsPage /></Suspense>} />
            <Route path="/admin/alerts" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminAlertsPage /></Suspense>} />
            <Route path="/admin/edge-logs" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminEdgeFunctionLogsPage /></Suspense>} />
            <Route path="/admin/rate-limits" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminRateLimitPage /></Suspense>} />
            <Route path="/admin/api-status" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminApiStatusPage /></Suspense>} />
            <Route path="/admin/analytics" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminAnalyticsPage /></Suspense>} />
            <Route path="/admin/abandonment" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminAbandonmentPage /></Suspense>} />
            <Route path="/admin/leads" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminLeadsPage /></Suspense>} />
            <Route path="/admin/user-management" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminUserManagementPage /></Suspense>} />
            <Route path="/admin/maintenance" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminMaintenanceChecklistPage /></Suspense>} />
            <Route path="/admin/brand-kit" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminBrandKitPage /></Suspense>} />
            <Route path="/admin/blog" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminBlogPage /></Suspense>} />
            <Route path="/admin/blog-api" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminBlogApiPage /></Suspense>} />
            <Route path="/admin/blog-api/logs" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminBlogApiLogsPage /></Suspense>} />
            <Route path="/admin/settings" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminSettingsPage /></Suspense>} />
            <Route path="/admin/contracts" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminContractsPage /></Suspense>} />
            <Route path="/admin/homeowners" element={<Suspense fallback={<AdminLoadingSkeleton />}><AdminHomeownersPage /></Suspense>} />
          </Route>
              
              {/* Consumer pages */}
              <Route path="/consumer" element={<ConsumerLandingPage />} />
              <Route path="/consumer/get-started" element={<ConsumerGetStartedPage />} />
              <Route path="/consumer/login" element={<ConsumerLoginPage />} />
              <Route path="/consumer/forgot-password" element={<ConsumerForgotPasswordPage />} />
              <Route path="/consumer/reset-password" element={<Navigate to="/consumer/login" replace />} />
              <Route path="/consumer/dashboard" element={
                <ConsumerProvider>
                  <ConsumerProtectedRoute>
                    <Suspense fallback={<div className="min-h-screen bg-[#080c14] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#ffd700]" /></div>}>
                      <ConsumerDashboardPage />
                    </Suspense>
                  </ConsumerProtectedRoute>
                </ConsumerProvider>
              } />
              
              {/* Property Valuation - Public */}
              <Route path="/valuation" element={<PropertyValuationPage />} />
              
              {/* Seller Net Sheet - Public Tool */}
              <Route path="/seller-net-sheet" element={<SellerNetSheetPage />} />
              
              {/* Get Offer - Public Form */}
              <Route path="/get-offer" element={<GetOfferPage />} />
              
              {/* Legal pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
              
              {/* Public routes — :sellerId accepts either a UUID or a profile_slug */}
              <Route path="/public/seller/:sellerId" element={<PublicListingsPage />} />
              <Route path="/public/seller/:sellerId/listings" element={<PublicListingsPage />} />
              <Route path="/public/seller/:sellerId/property/:propertyId" element={<PublicPropertyDetailPage />} />
              
              {/* Blog routes */}
              <Route path="/blog" element={<Suspense fallback={<AdminLoadingSkeleton />}><BlogPage /></Suspense>} />
              <Route path="/blog/:slug" element={<Suspense fallback={<AdminLoadingSkeleton />}><BlogArticlePage /></Suspense>} />
              
              {/* Seller routes */}
              <Route path="/seller" element={<LandingPage />} />
              <Route path="/seller/faq" element={<SellerFAQPage />} />
              
              {/* Email change callback */}
              <Route path="/auth/callback/email-change" element={<EmailChangeCallbackPage />} />
              
              {/* Seller Auth routes */}
              <Route element={<SellerAuthLayout />}>
                <Route path="/seller/login" element={<LoginPage />} />
                <Route path="/seller/register" element={<RegisterPage />} />
                <Route path="/seller/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/seller/reset-password" element={<Navigate to="/seller/login" replace />} />
              </Route>
              
              {/* Protected Seller routes */}
              <Route element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }>
                <Route path="/seller/dashboard" element={<DashboardPage />} />
                <Route path="/seller/properties/new" element={<NewPropertyPage />} />
                <Route path="/seller/properties/active" element={<ActiveDealsPage />} />
                <Route path="/seller/properties/closed" element={<ClosedDealsPage />} />
                <Route path="/seller/properties/:id" element={<PropertyDetailPage />} />
                <Route path="/seller/properties/:id/edit" element={<EditPropertyPage />} />
                <Route path="/seller/profile" element={<ProfilePage />} />
                <Route path="/seller/notifications" element={<NotificationsPage />} />
                <Route path="/seller/search-buyers" element={<SearchBuyersPage />} />
                <Route path="/seller/buyer-lists" element={<BuyerListsPage />} />
                <Route path="/seller/buyer-lists/:listId" element={<BuyerListDetailPage />} />
                <Route path="/seller/buyer-lists/:listId/edit" element={<EditBuyerListPage />} />
                <Route path="/seller/manage-subscription" element={<ManageSubscriptionPage />} />
                <Route path="/seller/offers" element={<SellerOffersPage />} />
                <Route path="/seller/search-properties" element={<SellerPropertySearchPage />} />
                <Route path="/seller/help" element={<SellerHelpHomePage />} />
                <Route path="/seller/help/articles" element={<HelpCenterPage />} />
                <Route path="/seller/deal-tracker" element={<DealTrackerPage />} />
                <Route path="/seller/tools" element={<SellerToolsPage />} />
                <Route path="/seller/tools/repair-estimator" element={
                  <Suspense fallback={<AdminLoadingSkeleton />}><RepairEstimatorPage /></Suspense>
                } />
                <Route path="/seller/tools/profit-calculator" element={
                  <Suspense fallback={<AdminLoadingSkeleton />}><ProfitCalculatorPage /></Suspense>
                } />
              </Route>
              
              {/* Legacy seller route redirects */}
              <Route path="/login" element={<Navigate to="/seller/login" replace />} />
              <Route path="/register" element={<Navigate to="/seller/register" replace />} />
              <Route path="/forgot-password" element={<Navigate to="/seller/forgot-password" replace />} />
              <Route path="/reset-password" element={<Navigate to="/seller/reset-password" replace />} />
              <Route path="/dashboard" element={<Navigate to="/seller/dashboard" replace />} />
              <Route path="/properties/*" element={<Navigate to="/seller/properties" replace />} />
              <Route path="/profile" element={<Navigate to="/seller/profile" replace />} />
              
              {/* Buyer routes */}
              <Route path="/buyer" element={<BuyerLandingPage />} />
              <Route path="/buyer/faq" element={<BuyerFAQPage />} />
              <Route path="/buyer/pricing" element={<BuyerPricingPage />} />
              {/* Buyer Auth routes */}
              <Route element={<BuyerAuthLayout />}>
                <Route path="/buyer/login" element={<BuyerLoginPage />} />
                <Route path="/buyer/register" element={<BuyerRegisterPage />} />
                <Route path="/buyer/forgot-password" element={<BuyerForgotPasswordPage />} />
                <Route path="/buyer/reset-password" element={<Navigate to="/buyer/login" replace />} />
              </Route>
              
              {/* Buyer upgrade page */}
              <Route path="/buyer/upgrade" element={
                <BuyerProtectedRoute requiresSubscription={false}>
                  <Suspense fallback={<AdminLoadingSkeleton />}><BuyerUpgradePage /></Suspense>
                </BuyerProtectedRoute>
              } />
              
              {/* Seller upgrade page */}
              <Route path="/seller/upgrade" element={
                <ProtectedRoute>
                  <SellerUpgradePage />
                </ProtectedRoute>
              } />
              
              {/* Protected Buyer routes */}
              <Route element={
                <ProtectedRoute>
                  <BuyerLayout />
                </ProtectedRoute>
              }>
                <Route path="/buyer/dashboard" element={<Suspense fallback={<AdminLoadingSkeleton />}><BuyerDashboardPage /></Suspense>} />
                <Route path="/buyer/profile" element={<ProfilePage />} />
                <Route path="/buyer/manage-subscription" element={<Suspense fallback={<AdminLoadingSkeleton />}><BuyerManageSubscriptionPage /></Suspense>} />
                
                {/* Routes that require active subscription */}
                <Route path="/buyer/search" element={
                  <BuyerProtectedRoute requiresSubscription={true}>
                    <Suspense fallback={<AdminLoadingSkeleton />}><PropertySearchPage /></Suspense>
                  </BuyerProtectedRoute>
                } />
                <Route path="/buyer/saved-properties" element={
                  <BuyerProtectedRoute requiresSubscription={true}>
                    <Suspense fallback={<AdminLoadingSkeleton />}><SavedPropertiesPage /></Suspense>
                  </BuyerProtectedRoute>
                } />
                <Route path="/buyer/notifications" element={
                  <BuyerProtectedRoute requiresSubscription={true}>
                    <Suspense fallback={<AdminLoadingSkeleton />}><BuyerNotificationsPage /></Suspense>
                  </BuyerProtectedRoute>
                } />
                <Route path="/buyer/sellers" element={
                  <BuyerProtectedRoute requiresSubscription={true}>
                    <Suspense fallback={<AdminLoadingSkeleton />}><FavoriteSellersPage /></Suspense>
                  </BuyerProtectedRoute>
                } />
                <Route path="/buyer/sellers/:sellerId/listings" element={
                  <BuyerProtectedRoute requiresSubscription={true}>
                    <Suspense fallback={<AdminLoadingSkeleton />}><SellerListingsPage /></Suspense>
                  </BuyerProtectedRoute>
                } />
                <Route path="/buyer/properties/:id" element={
                  <BuyerProtectedRoute requiresSubscription={true}>
                    <Suspense fallback={<AdminLoadingSkeleton />}><BuyerPropertyDetailPage /></Suspense>
                  </BuyerProtectedRoute>
                } />
                <Route path="/buyer/offers" element={
                  <BuyerProtectedRoute requiresSubscription={true}>
                    <Suspense fallback={<AdminLoadingSkeleton />}><BuyerOffersPage /></Suspense>
                  </BuyerProtectedRoute>
                } />
                <Route path="/buyer/repair-estimator" element={
                  <BuyerProtectedRoute requiresSubscription={true}>
                    <Suspense fallback={<AdminLoadingSkeleton />}><RepairEstimatorPage /></Suspense>
                  </BuyerProtectedRoute>
                } />
                <Route path="/buyer/profit-calculator" element={
                  <BuyerProtectedRoute requiresSubscription={true}>
                    <Suspense fallback={<AdminLoadingSkeleton />}><ProfitCalculatorPage /></Suspense>
                  </BuyerProtectedRoute>
                } />
                <Route path="/buyer/help" element={
                  <BuyerProtectedRoute>
                    <Suspense fallback={<AdminLoadingSkeleton />}><BuyerHelpHomePage /></Suspense>
                  </BuyerProtectedRoute>
                } />
                <Route path="/buyer/help/articles" element={
                  <BuyerProtectedRoute>
                    <Suspense fallback={<AdminLoadingSkeleton />}><BuyerHelpCenterPage /></Suspense>
                  </BuyerProtectedRoute>
                } />
              </Route>
              
                  {/* Catch-all route */}
                  <Route path="*" element={<NotFound />} />
                    </Routes>
                  </TourProvider>
                </SubscriptionProvider>
              </BuyerProvider>
            </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
  );
};

export default App;
